import makeStyles from "@material-ui/core/styles/makeStyles";
import React, { FC } from "react";
import { lpResponsiveStyles, SYDM2_SLUG } from "./constants";
import { InView } from "react-intersection-observer";
import "swiper/swiper-bundle.css";
import { Swiper, SwiperSlide } from "swiper/react";
import SwiperCore, { Pagination } from "swiper";
import useTheme from "@material-ui/core/styles/useTheme";
import { useMediaQuery } from "@material-ui/core";
import Link from "next/link";
import {
  LazyLoadComponent,
  LazyLoadImage,
} from "react-lazy-load-image-component";

const useStyles = makeStyles((theme) => {
  const root = lpResponsiveStyles(theme, (baseWidth) => ({
    position: "relative",
    backgroundColor: "#205F33",
  }));
  const courseIntro = lpResponsiveStyles(theme, (baseWidth) => ({
    position: "relative",
    backgroundSize: "100%",
    height: `calc(${baseWidth} * 1.598726115)`,
    width: baseWidth,
  }));
  const textArea = lpResponsiveStyles(theme, (baseWidth) => ({
    width: baseWidth,
    bottom: `calc(${baseWidth} * 1.598726115 * 0.06)`,
    fontSize: `calc(${baseWidth} * 0.025)`,
  }));
  const container = lpResponsiveStyles(theme, (baseWidth) => ({
    width: baseWidth,
    "& .swiper-pagination-bullets": {
      bottom: `calc(${baseWidth} *  1.598726115 * 0.15)`,
      "& .swiper-pagination-bullet": {
        margin: `0 10px`,
        opacity: 1,
        background: "white",
        "&.swiper-pagination-bullet-active": {
          width: "15px",
          height: "15px",
          background: "none",
          backgroundImage: "url(/img/lp/keijiban_petal.png)",
          backgroundSize: "100%",
          backgroundRepeat: "no-repeat",
          borderRadius: "0%",
        },
      },
    },
    "& .swiper-slide img": {
      position: "absolute",
      top: `calc(${baseWidth} * 1.598726115 * 0.17)`,
      left: `calc(${baseWidth} * 0.075)`,
      width: `calc(${baseWidth} * 0.85)`,
    },
    "& .swiper-slide:first-child img": {
      left: `calc(${baseWidth} * 0.08)`,
    },
    "& .swiper-slide:last-child img": {
      left: `calc(${baseWidth} * 0.05)`,
    },
  }));
  const containerPlaceholder = lpResponsiveStyles(theme, (baseWidth) => ({
    width: baseWidth,
    height: `calc(${baseWidth} *  1.598726115)`,
    backgroundColor: "#205F33",
  }));
  const courseContent = lpResponsiveStyles(theme, (baseWidth) => ({}));
  return {
    root,
    container,
    containerPlaceholder,
    courseIntro,
    courseContent,
    textArea: {
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      lineHeight: "1.3",
      position: "absolute",
      color: "white",
      zIndex: 2,
      ...textArea,
    },
    swiperRightAllow: {
      position: "absolute",
      top: "100px",
    },
    swiperLeftAllow: {
      position: "absolute",
      top: "100px",
    },
  };
});

SwiperCore.use([Pagination]);

const obverseSlideInfoList = [
  { background: "url(/img/lp/keijiban1.jpg)", poster: "/img/lp/poster1.png", alt: "生徒会役員共クラウドファンディングの会計コース", },
  { background: "url(/img/lp/keijiban2.jpg)", poster: "/img/lp/poster2.png", alt: "生徒会役員共クラウドファンディングの書紀コース", },
  { background: "url(/img/lp/keijiban3.jpg)", poster: "/img/lp/poster3.png", alt: "生徒会役員共クラウドファンディングの副会長コース", },
  { background: "url(/img/lp/keijiban4.jpg)", poster: "/img/lp/poster4.png", alt: "生徒会役員共クラウドファンディングの会長コース", },
  { background: "url(/img/lp/keijiban5.jpg)", poster: "/img/lp/poster5.png", alt: "生徒会役員共クラウドファンディングの生徒総会コース", },
];
const reverseSlideInfoList = [
  {
    background: "url(/img/lp/keijiban1.jpg)",
    poster: "/img/lp/poster1_reverse.png",
    alt: "生徒会役員共クラウドファンディングの会計コース(裏)",
  },
  {
    background: "url(/img/lp/keijiban2.jpg)",
    poster: "/img/lp/poster2_reverse.png",
    alt: "生徒会役員共クラウドファンディングの書紀コース(裏)",
  },
  {
    background: "url(/img/lp/keijiban3.jpg)",
    poster: "/img/lp/poster3_reverse.png",
    alt: "生徒会役員共クラウドファンディングの副会長コース(裏)",
  },
  {
    background: "url(/img/lp/keijiban4.jpg)",
    poster: "/img/lp/poster4_reverse.png",
    alt: "生徒会役員共クラウドファンディングの会長コース(裏)",
  },
  {
    background: "url(/img/lp/keijiban5.jpg)",
    poster: "/img/lp/poster5_reverse.png",
    alt: "生徒会役員共クラウドファンディングの生徒総会コース(裏)",
  },
];

type Props = {
  contentRoot: Element;
  onView?: (inView: boolean) => void;
};
export const SpCourseIntroduction: FC<Props> = (props: Props) => {
  const classes = useStyles();
  const theme = useTheme();
  const isSp = useMediaQuery(theme.breakpoints.down("sm"));
  return (
    <InView
      root={isSp ? null : props.contentRoot}
      rootMargin={"-50% 0px -40% 0px"}
      threshold={0}
      onChange={(inView, entry) => {
        props.onView && props.onView(inView);
      }}
      className={classes.root}
    >
      <Swiper
        slidesPerView={1}
        pagination={{ clickable: true, type: "bullets" }}
        className={classes.container}
      >
        {obverseSlideInfoList.map((slideInfo, index) => (
          <SwiperSlide key={index}>
            <div
              style={{ backgroundImage: slideInfo.background }}
              className={classes.courseIntro}
            >
              <Link href={`/project/${SYDM2_SLUG}`}>
                <LazyLoadImage
                  effect="opacity"
                  src={slideInfo.poster}
                  className={classes.courseContent}
                  width="100%"
                  alt={slideInfo.alt}
                />
              </Link>
            </div>
          </SwiperSlide>
        ))}
      </Swiper>
      <TextArea />
    </InView>
  );
};

export const SpCourseIntroductionReverse: FC<Props> = (props: Props) => {
  const classes = useStyles();
  const theme = useTheme();
  const isSp = useMediaQuery(theme.breakpoints.down("sm"));
  return (
    <InView
      root={isSp ? null : props.contentRoot}
      rootMargin={"-80% 0px -10% 0px"}
      threshold={0}
      onChange={(inView, entry) => {
        props.onView && props.onView(inView);
      }}
      className={classes.root}
    >
      <LazyLoadComponent
        placeholder={<div className={classes.containerPlaceholder} />}
      >
        <Swiper
          slidesPerView={1}
          pagination={{ clickable: true, type: "bullets" }}
          className={classes.container}
        >
          {reverseSlideInfoList.map((slideInfo, index) => (
            <SwiperSlide key={index}>
              <div
                style={{ backgroundImage: slideInfo.background }}
                className={classes.courseIntro}
              >
                <Link href={`/project/${SYDM2_SLUG}`}>
                  <LazyLoadImage
                    src={slideInfo.poster}
                    className={classes.courseContent}
                    width="100%"
                    alt={slideInfo.alt}
                  />
                </Link>
              </div>
            </SwiperSlide>
          ))}
        </Swiper>
        <TextArea />
      </LazyLoadComponent>
    </InView>
  );
};
const TextArea: FC = () => {
  const classes = useStyles();
  return (
    <div className={classes.textArea}>
      <div>
        <p>※画像はイメージです。</p>
        <p>※詳細・注意事項は本サイトでご確認ください。</p>
        <p>※値段表記は税込価格です。</p>
      </div>
    </div>
  );
};
